import { mdiAlert } from '@quasar/extras/mdi-v7';
// import { useLocalStorage } from '@vueuse/core';
import { ref } from 'vue';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useAxios } from '@vueuse/integrations/useAxios';
import type { StrapiResponseError } from 'src/types/strapi/strapiResponse';
import { Loading, LocalStorage, Notify } from 'quasar';
import { useCypher } from 'utils/cypher';
import { useMemoize } from '@vueuse/core';
import { t } from 'boot/i18n';
import type { MessageSchema } from 'boot/i18n';

export type StrapiProviders = 'local' | 'github' | 'google' | 'facebook';

const { encrypt: browserEncrypt, decrypt: browserDecrypt } = useCypher(
  'flashstudiessecretkey',
);

let jwt: string | null = '';
// find action
export type StrapiCustom =
  | `user-steps/${string}/reset`
  | `user-steps/${string}/progress`
  | `user-sections/${string}/reset`
  | `section/${string}/steps`
  | `user-sections/${string}/progress`
  | `user-courses/${string}/reset`
  | `user-courses/${string}/sections`
  | `user-courses/${string}/progress`
  | `user-courses/${string}/favorite`
  | `school-classes/by-semester/${string}`
  | `school-classes/${string}/detail`
  | `school-classes/${string}/assign-course`
  | `school-classes/${string}/assign-section`
  | `school-classes/${string}/assigned-courses`
  | `school-classes/${string}/assigned-sections`
  | `school-classes/${string}/course-progress/${string}`
  | `school-classes/${string}/section-progress/${string}`
  | `school-classes/${string}/students/${string}/stats-summary`
  | `school-classes/${string}/course-students/${string}/assignment`
  | `school-classes/${string}/course-students/${string}/assignment-bulk`
  | `school-classes/${string}/section-students/${string}/assignment`
  | `school-classes/${string}/section-students/${string}/assignment-bulk`
  | `school-students/${string}/profile/${string}`
  | `school-teachers/${string}/classes`
  | `school-enrollments/${string}`
  | 'school-enrollments/apply'
  | 'school-enrollments/join'
  | 'school-classes/available'
  | 'school-courses'
  | 'school-sections'
  | 'assign-license';
export type StrapiPlurals =
  | 'analytics'
  | 'avatars'
  | 'cities'
  | 'schools'
  | 'public-schools'
  | 'plans'
  | 'steps'
  | 'sections'
  | 'courses'
  | 'users'
  | 'profiles'
  | 'children'
  | 'semesters'
  | 'school-classes'
  | 'enrollments'
  | 'school-enrollments'
  | 'user-courses-me'
  | 'user-sections-me'
  | 'user-steps-me';
export type StrapiEndpoint =
  | 'analytics/summary'
  | 'analytics/me/summary'
  | `analytics/child/${number}/summary`
  | 'auth'
  | 'auth/local'
  | 'auth/local/register'
  | 'auth/forgot-password'
  | 'auth/change-password'
  | `/auth/${StrapiProviders}/callback`
  | 'liqpay-plugin/pay/checkout'
  | 'liqpay-plugin/order/me'
  | 'liqpay-plugin/pay/update_subscription'
  | 'register-presentation'
  | 'users/me'
  | 'users/me/profile'
  | 'users-permissions/users/me/profile'
  | 'users-permissions/users/child/new'
  | 'users-permissions/users/student/register'
  | 'users-permissions/users/child/token'
  | 'users-permissions/users/child/login'
  | 'users-permissions/users/child'
  | 'users-permissions/auth/providers'
  | 'user-steps'
  | 'user-stats'
  | 'user-sections'
  | 'user-courses'
  | 'upload/files'
  | StrapiPlurals
  | StrapiCustom;

export type StrapiEndpointId = `${StrapiEndpoint}/${string | number}`;

const suppressForbidenErrorRoutes = ['users/me'];

export const JWT_LOCAL_STORAGE_NAME = 'flushStudiesTokenForAuth';

export function getJwtFromLocalStorage(force?: boolean): string | null {
  if (!force && jwt && jwt.length > 0) return jwt;
  const token = LocalStorage.getItem<string>(JWT_LOCAL_STORAGE_NAME) || null;
  const encrypted = token && token.length > 0 ? token : null;
  jwt = encrypted ? browserDecrypt(encrypted) : null;
  return jwt;
}
export function setJwtToLocalStorage(token: string) {
  jwt = token;
  const encrypted = browserEncrypt(token);
  LocalStorage.setItem(JWT_LOCAL_STORAGE_NAME, encrypted);
}

export function removeJwtFromLocalStorage() {
  jwt = null;
  LocalStorage.removeItem(JWT_LOCAL_STORAGE_NAME);
}

console.log('VITE_BACKEND_URL', import.meta.env.VITE_BACKEND_URL);

export const baseUrl =
  import.meta.env.VITE_BACKEND_URL || 'http://localhost:1337';

const baseApiUrl =
  import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:1337/api';

// export function strapiGetBuilder(url: StrapiEndpoint, params = {}) {
//   return `${url}${qs.stringify(params, {
//     encodeValuesOnly: true,
//     addQueryPrefix: true,
//   })}`;
// }

const headers: Record<string, string> = {
  'Content-Type': 'application/json',
};

const instance = axios.create({
  baseURL: baseApiUrl,
  headers,
});

// Public auth endpoints are granted to the Public role only. Sending a (possibly
// stale) JWT makes Strapi check the user's own role instead, which lacks these
// permissions, so e.g. reset-password answers 403 for anyone with a leftover token.
const ANONYMOUS_URL_RE = /^\/?auth\/(forgot-password|reset-password)$/;

instance.interceptors.request.use((config) => {
  const token = getJwtFromLocalStorage();
  if (token && !ANONYMOUS_URL_RE.test(config.url || '')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A successful enrollment-gated request means the student now has a valid
// enrollment — clear the pending flag so a stale "awaiting approval" banner
// disappears without a full reload (e.g. after the teacher approves them).
instance.interceptors.response.use((response) => {
  if (ENROLLMENT_GATED_URL_RE.test(response.config?.url || '')) {
    hasNoActiveEnrollment.value = false;
  }
  return response;
});

interface StrapiOptions {
  suppressError?: boolean;
}

// The backend's `validSchoolEnrollment` middleware returns this exact 403 message
// when a school student has no active enrollment for the current term (e.g. they
// enrolled via a link and are still pending the teacher's approval). This is an
// expected, benign state — not an error to alarm the user with a red toast.
const NO_ACTIVE_ENROLLMENT_MESSAGE =
  'You have no active enrollment for the current term';

// Shared, reactive signal: true once any request has been rejected for lack of an
// active enrollment. The dashboard reads this to show a calm "awaiting approval"
// banner instead of a toast. Reset on sign-in/out and when a gated request succeeds.
export const hasNoActiveEnrollment = ref(false);

// Enrollment-gated endpoints (courses/sections/steps "me"): a successful response
// means the student now has a valid enrollment, so clear the pending flag.
const ENROLLMENT_GATED_URL_RE = /user-(courses|sections|steps)-me/;

// Strapi returns error messages as raw English strings. Map the ones we surface
// to users onto i18n keys; anything unmapped falls back to the backend text.
const backendMessageTranslationKeys: Record<string, keyof MessageSchema> = {
  [NO_ACTIVE_ENROLLMENT_MESSAGE]: 'ERROR_NO_ACTIVE_ENROLLMENT',
  // School-brand Google/Facebook login for an email with no account (oauth.ts).
  'No account exists for this email': 'ERROR_NO_ACCOUNT_FOR_EMAIL',
};

function translateBackendMessage(message: string | undefined): string {
  const key = message ? backendMessageTranslationKeys[message] : undefined;
  // `t` is assigned by the i18n boot file; guard against errors raised before it runs.
  if (key && typeof t === 'function') return t(key);
  if (message) return message;
  return typeof t === 'function'
    ? t('ERROR_FETCHING_DATA')
    : 'Error on fetching data';
}

// A single failure condition (e.g. a missing enrollment) is often hit by several
// concurrent requests during layout/drawer init. Collapse identical errors so the
// user sees one toast instead of one per call site.
const DUPLICATE_ERROR_WINDOW_MS = 5000;
const recentErrorNotifications = new Map<string, number>();

function isDuplicateErrorNotification(key: string): boolean {
  const now = performance.now();
  for (const [seenKey, seenAt] of recentErrorNotifications) {
    if (now - seenAt > DUPLICATE_ERROR_WINDOW_MS) {
      recentErrorNotifications.delete(seenKey);
    }
  }
  if (recentErrorNotifications.has(key)) return true;
  recentErrorNotifications.set(key, now);
  return false;
}

export function notifyError(err: unknown) {
  Loading.hide();
  if (!err) return;
  if ((<AxiosError>err).code === 'ERR_CANCELED') return;
  const { response } = err as AxiosError;
  const { error: strapiError } = (response?.data as StrapiResponseError) ?? {
    error: null,
  };
  console.debug('onFetchError', strapiError);
  const url = response?.config?.url;

  // "No active enrollment" is an expected pending state, not an error: record it so
  // the dashboard can show a friendly banner, and never surface the scary red toast.
  if (
    response?.status === 403 &&
    strapiError?.message === NO_ACTIVE_ENROLLMENT_MESSAGE
  ) {
    hasNoActiveEnrollment.value = true;
    return;
  }

  if (
    response?.status === 403 &&
    suppressForbidenErrorRoutes.some((route) => url?.startsWith(route))
  ) {
    console.debug('onFetchError - suppressErrorRoutes', url);
    return;
  }
  const duplicateKey = `${strapiError?.status}|${strapiError?.name}|${strapiError?.message}`;
  if (isDuplicateErrorNotification(duplicateKey)) {
    console.debug('onFetchError - duplicate suppressed', duplicateKey, url);
    return;
  }
  Notify.create({
    caption: `${strapiError?.name} (${strapiError?.status})`,
    message: translateBackendMessage(strapiError?.message),
    color: 'negative',
    icon: mdiAlert,
    position: 'bottom',
  });
}

export const useStrapiPut = (
  url: StrapiEndpoint | StrapiEndpointId,
  data = {},
  options: StrapiOptions = {},
) => {
  const {
    execute,
    data: responseData,
    error,
    isFinished,
  } = useAxios(url, { method: 'PUT', data }, instance, {
    immediate: false,
    onError: options?.suppressError ? () => {} : notifyError,
  });

  return {
    execute,
    data: responseData,
    error,
    isFinished,
  };
};

export const useMemoizedGet = useMemoize(
  async (
    url: StrapiEndpoint | StrapiEndpointId,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qs: Record<string, any>,
    options: StrapiOptions = {},
  ) => {
    const fullUrl = url.toString();
    const { execute, data, error, isFinished } = useAxios(
      fullUrl,
      { method: 'GET', params: qs },
      instance,
      {
        immediate: false,
        onError: options?.suppressError ? () => {} : notifyError,
      },
    );
    await execute();
    return {
      data,
      error,
      isFinished,
    };
  },
  {
    // Responses are scoped to the caller's token (e.g. /schools returns only the
    // schools the user belongs to). Signing in swaps the JWT without reloading the
    // SPA, so the key must include it — otherwise the next user is served the
    // previous user's cached response.
    getKey: (url, qs, options?: StrapiOptions) =>
      JSON.stringify([getJwtFromLocalStorage(), url.toString(), qs, options]),
  },
);

/**
 * Bumped on every cache clear. Watch it to reload anything built from a memoized
 * response, since clearing the cache does not by itself re-run the fetch.
 */
export const memoizedGetCacheVersion = ref(0);

/**
 * Drop every memoized GET response. Call when the active user changes, or when a
 * write invalidates user-scoped data (e.g. joining a class changes /schools).
 */
export function clearMemoizedGetCache() {
  useMemoizedGet.clear();
  memoizedGetCacheVersion.value += 1;
}

export const useStrapiGet = (
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  qs: { [key: string]: any } = {},
  options: StrapiOptions = {},
) => {
  const {
    execute: rawExecute,
    data: responseData,
    error,
    isFinished,
  } = useAxios(url, { method: 'GET', params: qs }, instance, {
    immediate: false,
    onError: options?.suppressError ? () => {} : notifyError,
  });

  // Wrapper that handles both HTTP errors and exceptions
  const execute = async (config?: Parameters<typeof rawExecute>[0]) => {
    try {
      await rawExecute(config);
      return { data: responseData.value, error: error.value };
    } catch (err) {
      console.error(`useStrapiGet: Exception during execute for ${url}:`, err);
      return { data: null, error: err };
    }
  };

  return {
    execute,
    data: responseData,
    error,
    isFinished,
  };
};

export const useStrapiPost = (
  url: StrapiEndpoint,
  data = {},
  options: StrapiOptions = {},
) => {
  const {
    execute,
    data: responseData,
    error,
    isFinished,
  } = useAxios(url, { method: 'POST', data }, instance, {
    immediate: false,
    onError: options?.suppressError ? () => {} : notifyError,
  });

  return {
    execute,
    data: responseData,
    error,
    isFinished,
  };
};

export const useStrapiDelete = (url: StrapiEndpointId) => {
  const {
    execute,
    data: responseData,
    error,
    isFinished,
  } = useAxios(url, { method: 'DELETE' }, instance, {
    immediate: false,
  });

  return {
    execute,
    data: responseData,
    error,
    isFinished,
  };
};
